import React, { useState } from 'react';
import Icon from './Icon';
import Reveal from './Reveal';
import { SITE_URL } from './SEO';
import './DownloadSection.css';

// One section, both apps, one QR code — meant to be the single stable link
// printed anywhere physical (branded shirts, flyers, posters), not just a
// page element. The QR/share target is this section's own anchor
// (#download), not a specific store, so the same code works for an iPhone
// or an Android, whoever scans it lands here and taps the right badge for
// their device.
const APPS = [
  {
    id: 'customer',
    name: 'BikesDrop',
    tagline: 'The Customer App',
    theme: 'teal',
    iosUrl: 'https://apps.apple.com/gh/app/bikesdrop/id6805873483',
    androidUrl: 'https://play.google.com/store/apps/details?id=com.bikesdrop.user',
  },
  {
    id: 'business',
    name: 'BikesDrop Business',
    tagline: 'For Riders, Truck Drivers, Merchants & Vendors',
    theme: 'amber',
    iosUrl: 'https://apps.apple.com/gh/app/bikesdrop-business/id6805875150',
    androidUrl: 'https://play.google.com/store/apps/details?id=com.bikesdrop.business',
    // Merchants and Vendors running a till/POS setup on a PC, not a phone
    // or tablet (Riders have no desktop use case, so the Windows build
    // itself only ever lets a Merchant or Vendor account in — see
    // bikesdrop-business's own main.dart/landing_screen.dart). Now live on
    // the Microsoft Store, so this links out there instead of serving the
    // standalone installer directly.
    windowsUrl: 'https://apps.microsoft.com/detail/9NZ1R9CM1TKC?hl=en-us&gl=US&ocid=pdpshare',
  },
];

const SHARE_TEXT =
  "BikesDrop — Ghana's all-in-one delivery & logistics app. Book a pickup, move cargo, run a campus errand, grab an Okada ride, or order food and groceries, all with live tracking. Riders, truck drivers, merchants and vendors earn with BikesDrop Business. Download both apps here:";

export default function DownloadSection() {
  const [shareState, setShareState] = useState('idle'); // idle | copied

  const handleShare = async () => {
    const shareUrl = `${SITE_URL}/#download`;
    const payload = { title: 'Download BikesDrop', text: SHARE_TEXT, url: shareUrl };
    if (navigator.share) {
      try {
        await navigator.share(payload);
      } catch (_) {
        // Cancelled or unsupported mid-call — the sheet closing is feedback enough.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT}\n${shareUrl}`);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2200);
    } catch (_) {
      /* Clipboard unavailable — nothing more to try here. */
    }
  };

  return (
    <section className="download section" id="download">
      <div className="container download__inner">
        <Reveal className="download__qr-col">
          <div className="download__qr-card">
            <img src="/assets/qr/download-qr.svg" alt="QR code linking to the BikesDrop app downloads" />
          </div>
          <button type="button" className="download__share" onClick={handleShare}>
            <Icon name="share" size={16} />
            {shareState === 'copied' ? 'Link copied!' : 'Share this link'}
          </button>
        </Reveal>

        <Reveal className="download__copy">
          <span className="eyebrow eyebrow--teal">Get Started</span>
          <h2 className="section-heading">Download BikesDrop</h2>
          <p className="section-sub">
            Scan the code, or tap a link below. Customers get the BikesDrop app, riders, truck
            drivers, merchants and vendors get BikesDrop Business.
          </p>

          <div className="download__apps">
            {APPS.map((app) => (
              <div className={`download__app download__app--${app.theme}`} key={app.id}>
                <div className="download__app-head">
                  <strong>{app.name}</strong>
                  <span>{app.tagline}</span>
                </div>
                <div className="download__badges">
                  <a
                    href={app.iosUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Download ${app.name} on the App Store`}
                  >
                    <img src="/assets/badges/app-store-badge.svg" alt="Download on the App Store" />
                  </a>
                  <a
                    href={app.androidUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Get ${app.name} on Google Play`}
                  >
                    <img src="/assets/badges/google-play-badge.png" alt="Get it on Google Play" />
                  </a>
                  {app.windowsUrl && (
                    <a
                      href={app.windowsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download__badge-windows"
                      aria-label={`Get ${app.name} for Windows on the Microsoft Store`}
                    >
                      <Icon name="download" size={16} />
                      Get it on Microsoft Store
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
